import {
  AnimationEvent,
  transition,
  trigger,
  useAnimation,
} from '@angular/animations';
import { NgSwitch, NgSwitchCase } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  inject,
} from '@angular/core';
import { fadeIn, fadeOut } from '@freelancer/animations';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor } from '@freelancer/ui/button';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor } from '@freelancer/ui/text';
import { Margin } from '../margin';
import {
  BannerAlertButton,
  BannerAlertLevel,
  BannerAlertType,
} from './banner-alert.types';

@Component({
  standalone: true,
  selector: 'fl-banner-alert',
  imports: [ButtonComponent, IconModule, NgSwitch, NgSwitchCase],
  template: `
    <div
      class="Container"
      [attr.data-compact]="compact"
      [attr.data-bordered]="bordered"
    >
      @if (!compact) {
        <div
          class="mr-xsmall mobile:hide"
          [ngSwitch]="type"
        >
          <fl-icon
            *ngSwitchCase="BannerAlertType.INFO"
            [color]="IconColor.SECONDARY"
            [name]="'ui-info'"
            [size]="IconSize.MID"
            [useIconFont]="true"
          />
          <fl-icon
            *ngSwitchCase="BannerAlertType.ERROR"
            [name]="'ui-error'"
            [size]="IconSize.MID"
            [color]="IconColor.ERROR"
            [useIconFont]="true"
          />
          <fl-icon
            *ngSwitchCase="BannerAlertType.SUCCESS"
            [color]="IconColor.SUCCESS"
            [name]="'ui-check-in-circle-v2'"
            [size]="IconSize.MID"
            [useIconFont]="true"
          />
          <fl-icon
            *ngSwitchCase="BannerAlertType.WARNING"
            [color]="IconColor.CONTEST"
            [name]="'ui-warning'"
            [size]="IconSize.MID"
            [useIconFont]="true"
          />
          <fl-icon
            *ngSwitchCase="BannerAlertType.CRITICAL"
            [color]="IconColor.LIGHT"
            [fillIconFont]="true"
            [name]="'ui-error'"
            [size]="IconSize.MID"
            [useIconFont]="true"
          />
        </div>
      }
      <div
        class="Content mr-small {{
          compact
            ? 'tablet:mr-small'
            : !closeable && !bordered
              ? 'tablet:mr-none'
              : 'tablet:mr-large'
        }}"
      >
        @if (bannerTitle) {
          <p
            [class]="getMarginBottomClass()"
            [class.text-light]="
              fontColor ? fontColor : type === BannerAlertType.CRITICAL
            "
            [class.text-foreground]="
              !(fontColor ? fontColor : type === BannerAlertType.CRITICAL)
            "
          >
            {{ bannerTitle }}
          </p>
        }
        <div
          class="Description text-body-16 font-normal {{
            bannerAlertButton ? 'mb-xxsmall' : 'mb-none'
          }}"
          [class.text-light]="
            fontColor ? fontColor : type === BannerAlertType.CRITICAL
          "
          [class.text-foreground]="
            !(fontColor ? fontColor : type === BannerAlertType.CRITICAL)
          "
        >
          <ng-content />
        </div>
        @if (bannerAlertButton) {
          <fl-button
            class="BannerAlertButton"
            [link]="bannerAlertButton.link"
            [fragment]="bannerAlertButton.fragment"
            [queryParams]="bannerAlertButton.queryParams"
            [color]="
              bannerAlertButton.color ??
              (type === BannerAlertType.CRITICAL
                ? ButtonColor.TEXT_LIGHT
                : ButtonColor.TEXT_BLUE)
            "
            [disabled]="bannerAlertButton.busy"
            (click)="handleButtonClick()"
          >
            {{ bannerAlertButton.text }}
          </fl-button>
        }
      </div>

      @if (closeable) {
        <fl-icon
          class="CloseButton"
          i18n-label="Banner alert close button"
          label="Close banner alert"
          [color]="
            type === BannerAlertType.CRITICAL
              ? IconColor.LIGHT
              : IconColor.FOREGROUND
          "
          [name]="'ui-close'"
          [size]="IconSize.MID"
          [useIconFont]="true"
          (click)="hide()"
        />
      }
    </div>
  `,
  styleUrls: ['./banner-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('hideElement', [
      transition('visible => hidden', [
        useAnimation(fadeOut, {
          params: { duration: '500' },
        }),
      ]),
      transition('* => visible', [
        useAnimation(fadeIn, {
          params: { duration: '250' },
        }),
      ]),
    ]),
  ],
})
export class BannerAlertComponent {
  private changeDetectorRef = inject(ChangeDetectorRef);

  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  IconColor = IconColor;
  IconSize = IconSize;

  @Input() bannerTitle?: string;
  @Input() bannerAlertButton?: BannerAlertButton;
  @Input() @HostBinding('attr.data-type') type = BannerAlertType.INFO;
  @Input() @HostBinding('attr.data-closeable') closeable = true;
  @Input() compact = false;
  @Input() @HostBinding('attr.data-level') level = BannerAlertLevel.NONE;
  @Input() @HostBinding('attr.data-bordered') bordered = true;
  @Input() fontColor?: FontColor;
  @Input() marginBottom: Margin = 'xxxsmall';
  @Input() @HostBinding('class.IsMessaging') isMessaging?: boolean;

  @Output() buttonClicked = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  @HostBinding('attr.role') readonly role = 'alert';
  @HostBinding('class.IsHidden') bannerAlertHidden = false;
  @HostBinding('@hideElement') state = 'visible';

  getMarginBottomClass(): string {
    return `text-body-16 font-bold mb-${this.marginBottom}`;
  }

  @HostListener('@hideElement.done', ['$event'])
  animationDone(event: AnimationEvent): void {
    if (event.toState === 'hidden') {
      this.bannerAlertHidden = true;
    }
  }

  handleButtonClick(): void {
    this.buttonClicked.emit();
  }

  hide(): void {
    this.state = 'hidden';
    this.close.emit();
    this.changeDetectorRef.markForCheck();
  }

  show(): void {
    this.state = 'visible';
    this.bannerAlertHidden = false;
    this.changeDetectorRef.markForCheck();
  }
}
